/**
 * Addresses and districts (#341): one card per distinct address, whichever sources give it (Home and Office from CE21's
 * Accounts Report, the state licence list's, CE21's main address), Home first. Each card: its sources, the address, and
 * the US House, State Senate and State House it falls in, each a link to the people in that district by that source.
 * A source that found other districts for the same address, or none yet, says so beside the others: no district a
 * filter reads is ever hidden, and none is borrowed from another source. A district that differs from Home's is set
 * a little stronger.
 */
import type { AddressDistricts, DistrictAddress, LegislativeDistricts, LegislativeOfficial, MemberDetail, PersonAddress } from "../../api/dashboard.types";
import { href } from "../app/links";
import { cx, focusRing, SheetSection } from "../kit";
import { DEFAULT_DISTRICT_ADDRESS } from "../screens/people/facets";
import { fmtDate, sameAddress, type Place } from "./format";

type SourceKey = "home" | "office" | "state_list" | "on_file";
type Districts = Omit<AddressDistricts, "confidence"> & { confidence: AddressDistricts["confidence"] };

interface Source {
  key: SourceKey;
  place: Place;
  country: string;
  districts: Districts | null;
}

const LABEL: Readonly<Record<SourceKey, string>> = { home: "Home", office: "Office", state_list: "State list", on_file: "CE21 main" };

const CHAMBERS = [
  ["US House", "usHouse"],
  ["State Senate", "stateSenate"],
  ["State House", "stateHouse"],
] as const;
type Chamber = (typeof CHAMBERS)[number][1];

const has = (d: Districts | null | undefined): d is Districts => !!d && CHAMBERS.some(([, k]) => d[k]);

/** Why an address has no districts, said once and plainly. */
function noDistricts(districts: Districts | null): string {
  if (!districts || districts.confidence === "error") return "Districts coming";
  if (districts.confidence === "outside_us") return "Outside the US";
  if (districts.confidence === "no_address") return "No street, so no districts";
  return "No districts found";
}

const fromPerson = (address: PersonAddress | null | undefined, key: SourceKey): Source | null =>
  address ? { key, place: { line1: address.line1, line2: address.line2, city: address.city, state: address.state, zip: address.postalCode }, country: address.country, districts: address.districts } : null;

function fromOnFile(onFile: LegislativeDistricts | null): Source | null {
  if (!onFile?.address) return null;
  const { street, city, state, zip } = onFile.address;
  return { key: "on_file", place: { line1: street, line2: "", city, state, zip }, country: "", districts: onFile };
}

/** The sources' addresses as cards: one per distinct address, in the order Home, Office, State list, CE21 main. */
function cardsOf(sources: Source[]): Source[][] {
  const cards: Source[][] = [];
  for (const s of sources) {
    const card = cards.find((c) => sameAddress(c[0]!.place, s.place));
    if (card) card.push(s);
    else cards.push([s]);
  }
  return cards;
}

/** A card's sources grouped by what they found: the same districts (or the same reason for none) say it once. */
function groupsOf(card: Source[]): Source[][] {
  const sig = (s: Source) => (has(s.districts) ? CHAMBERS.map(([, k]) => s.districts?.[k]).join("|") : noDistricts(s.districts));
  const groups = new Map<string, Source[]>();
  for (const s of card) groups.set(sig(s), [...(groups.get(sig(s)) ?? []), s]);
  return [...groups.values()];
}

const words = (sources: Source[]) => sources.map((s) => LABEL[s.key]).join(" · ");

/** The address in two lines: the street, then the town. The state list's capitals are shown only when it is all there is. */
function AddressLines({ card }: { card: Source[] }) {
  const shown = card.find((s) => s.key !== "state_list") ?? card[0]!;
  const { line1, line2, city, state, zip } = shown.place;
  const town = [city, [state, zip].filter(Boolean).join(" ")].filter(Boolean).join(", ");
  const abroad = shown.country && !/^(united states|usa?|us)$/i.test(shown.country.trim()) ? shown.country : "";
  return (
    <p className="flex flex-col text-body leading-5 text-cell-foreground">
      {[[line1, line2].filter(Boolean).join(", "), [town, abroad].filter(Boolean).join(", ")]
        .filter(Boolean)
        .map((line) => (
          <span key={line}>{line}</span>
        ))}
    </p>
  );
}

/** Michigan's FIPS code; another state's district carries its code ("OH 7"), so two states' 7s never read alike. */
const HOME_STATE_FIPS = "26";
const FIPS_USPS: Readonly<Record<string, string>> = Object.fromEntries(
  "01AL 02AK 04AZ 05AR 06CA 08CO 09CT 10DE 11DC 12FL 13GA 15HI 16ID 17IL 18IN 19IA 20KS 21KY 22LA 23ME 24MD 25MA 26MI 27MN 28MS 29MO 30MT 31NE 32NV 33NH 34NJ 35NM 36NY 37NC 38ND 39OH 40OK 41OR 42PA 44RI 45SC 46SD 47TN 48TX 49UT 50VT 51VA 53WA 54WV 55WI 56WY 72PR"
    .split(" ")
    .map((x) => [x.slice(0, 2), x.slice(2)]),
);
const seat = (key: string) => {
  const [fips = "", n = ""] = key.split("-");
  return fips === HOME_STATE_FIPS ? n : `${FIPS_USPS[fips] ?? fips} ${n}`;
};

const PILL = cx("inline-flex h-6 max-w-full items-center self-start rounded-md border-hair px-2 text-meta leading-none whitespace-nowrap", focusRing);

/** One chamber: a link to the people in its district by this source, and who sits for it when VoterVoice matched. */
function District({ title, chamber, source, value, officials, differs }: { title: string; chamber: Chamber; source: SourceKey; value: string; officials: LegislativeOfficial[] | undefined; differs: string }) {
  if (!value) return <span className="py-0.5 text-meta leading-5 text-muted-foreground">{title} —</span>;
  const of: DistrictAddress = source;
  const param = of === DEFAULT_DISTRICT_ADDRESS ? "" : `&districtAddress=${of}`;
  return (
    <>
      <a
        href={href(`/people?view=all&${chamber}=${value}${param}`)}
        aria-label={`${title} ${seat(value)}${differs ? `, not ${differs}'s` : ""}`}
        className={cx(PILL, differs ? "border-border-hover bg-card font-medium text-foreground shadow-frame" : "border-border bg-secondary text-secondary-foreground hover:bg-card")}
      >
        {title} {seat(value)}
      </a>
      {officials?.map((o) => (
        <span key={o.name} data-name title={o.name} className="truncate text-meta leading-[18px] text-muted-foreground">
          {o.name}
        </span>
      ))}
    </>
  );
}

/** One group's districts (or why it has none), under the names of its sources when the card's sources disagree. */
function Group({ group, labelled, baseline }: { group: Source[]; labelled: boolean; baseline: Source | null }) {
  const first = group[0]!;
  const d = first.districts;
  return (
    <div className="flex flex-col gap-1.5">
      {labelled && <span className="text-caption leading-4 font-medium text-muted-foreground">{words(group)}</span>}
      {has(d) ? (
        <ul className="grid grid-cols-3 gap-x-3" aria-label={labelled ? `Districts by ${words(group)}` : "Districts"}>
          {CHAMBERS.map(([title, chamber]) => (
            <li key={chamber} className="flex min-w-0 flex-col gap-1">
              <District title={title} chamber={chamber} source={first.key} value={d[chamber]} officials={d.officials?.[chamber]} differs={baseline?.districts && baseline.districts !== d && d[chamber] && baseline.districts[chamber] && d[chamber] !== baseline.districts[chamber] ? LABEL[baseline.key] : ""} />
            </li>
          ))}
        </ul>
      ) : (
        <span className="text-meta leading-[18px] text-muted-foreground">{noDistricts(d)}</span>
      )}
    </div>
  );
}


/** Where the addresses came from, and how the districts were matched: each said once, under the cards. */
function Notes({ shown, addresses }: { shown: Districts[]; addresses: MemberDetail["addresses"] }) {
  const census = shown.filter((d) => d.method === "census_onelineaddress");
  const censusLine = census.length === 0 ? "" : census.some((d) => d.stateFips === HOME_STATE_FIPS) ? "Matched by the US Census, whose State Senate lines are the map that takes effect in January 2027." : "Matched by the US Census.";
  const voterVoice = shown.some((d) => d.method === "votervoice");
  const latest = (...days: Array<string | null | undefined>) => days.filter((d): d is string => !!d).sort().at(-1);
  // The day of the load that last confirmed each, not the day the address first arrived.
  const day = (a: PersonAddress | null | undefined) => (a ? (a.loadedAt === undefined ? a.importedAt : a.loadedAt) : null);
  const file = latest(day(addresses.home), day(addresses.office));
  const list = latest(day(addresses.stateList));
  const from = [file && `Home and office from CE21's Accounts Report, loaded ${fmtDate(file)}`, list && `State list loaded ${fmtDate(list)}`].filter(Boolean).join(" · ");
  return (
    <div className="flex flex-col gap-2 text-meta leading-[18px] text-muted-foreground">
      {censusLine && <p>{censusLine}</p>}
      {voterVoice && (
        <div className="flex items-center justify-between gap-3">
          <span>Matched to the legislators in office now.</span>
          {/* VoterVoice's API terms: acknowledge it wherever its data shows. */}
          <img src="/poweredby-votervoice.png" alt="Powered by VoterVoice" className="h-6 w-auto shrink-0" />
        </div>
      )}
      {from && <p>{from}</p>}
    </div>
  );
}

/** Every address the person has, one card per distinct one, with its districts. */
export function AddressesSection({ addresses, onFile }: { addresses: MemberDetail["addresses"]; onFile: LegislativeDistricts | null }) {
  const own = [fromPerson(addresses.home, "home"), fromPerson(addresses.office, "office"), fromPerson(addresses.stateList, "state_list")].filter((s): s is Source => !!s);
  // CE21's main address with no street says nothing the others do not, unless it is all there is.
  const main = fromOnFile(onFile);
  const sources = main && (main.place.line1 || own.length === 0) ? [...own, main] : own;
  if (sources.length === 0) return null;
  const cards = cardsOf(sources);
  // Home's districts (or the first card's) are what the others are read against.
  const baseline = cards.flat().find((s) => has(s.districts)) ?? null;
  const missing = [!addresses.home && "No home address", !addresses.office && "No office address", addresses.stateList === null && addresses.licensed !== false && (addresses.stateListAbsent ? "Not on the state list" : "No state list address yet")].filter(Boolean).join(" · ");
  const shown = cards.flat().map((s) => s.districts).filter(has);
  return (
    <SheetSection title="Addresses and districts">
      <ul className="flex flex-col" aria-label="Addresses">
        {cards.map((card) => {
          const groups = groupsOf(card);
          return (
            <li key={card.map((s) => s.key).join()} aria-label={words(card)} className="flex flex-col gap-3 border-t border-rule py-4 first:border-t-0 first:pt-0">
              <div className="flex flex-col gap-1">
                <span className="text-body leading-5 font-medium text-foreground">{words(card)}</span>
                <AddressLines card={card} />
              </div>
              {groups.map((group) => (
                <Group key={group.map((s) => s.key).join()} group={group} labelled={groups.length > 1} baseline={baseline} />
              ))}
            </li>
          );
        })}
      </ul>
      {missing && <p className="text-meta leading-[18px] text-muted-foreground">{missing}</p>}
      <Notes shown={shown} addresses={addresses} />
    </SheetSection>
  );
}
